// src/components/common/AuthorizationModal/AuthorizationModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Close as CloseIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { AutorizacionesApi } from '../../../services/api/autorizacionesApi';
import './authorizationModal.scss';

interface AuthorizationModalProps {
  open: boolean;
  onClose: () => void;
  action: string;
  actionName: string;
  actionData: any;
  onAuthorized: (code: string) => void;
}

const steps = ['Solicitar Código', 'Ingresar Código', 'Confirmación'];

const AuthorizationModal: React.FC<AuthorizationModalProps> = ({
  open,
  onClose,
  action,
  actionName,
  actionData,
  onAuthorized,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [authCode, setAuthCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(30 * 60); // 30 minutos
  const [copied, setCopied] = useState(false);
  // Polling para "esperando aprobación"
  const [waitingApproval, setWaitingApproval] = useState(false);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (open) {
      resetModal();
    }
  }, [open]);

  useEffect(() => {
    if (authCode && activeStep === 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 0) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [authCode, activeStep]);

  const resetModal = () => {
    setActiveStep(0);
    setAuthCode('');
    setInputCode('');
    setError('');
    setSuccess('');
    setCountdown(30 * 60);
    setCopied(false);
    setWaitingApproval(false);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const handleSolicitarCodigo = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await AutorizacionesApi.solicitarAutorizacion({
        codigoAccion: action,
        datosAccion: actionData,
      });

      setAuthCode(response.CodigoAutorizacion);
      setSuccess('Código generado exitosamente');

      // Iniciar polling para verificar si fue aprobado
      setWaitingApproval(true);
      pollRef.current = setInterval(async () => {
        try {
          const estado = await AutorizacionesApi.obtenerEstadoSolicitud(response.CodigoAutorizacion);
          if (estado.existe && estado.estado === 'AUTORIZADA') {
            if (pollRef.current) clearInterval(pollRef.current);
            setWaitingApproval(false);
            setSuccess('¡Autorización aprobada!');
            setTimeout(() => {
              onAuthorized(response.CodigoAutorizacion);
              onClose();
            }, 1500);
          } else if (estado.existe && estado.estado === 'RECHAZADA') {
            if (pollRef.current) clearInterval(pollRef.current);
            setWaitingApproval(false);
            setError('La autorización fue rechazada');
          }
        } catch {
          // silenciar errores de polling
        }
      }, 5000); // Polling cada 5s
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al solicitar autorización');
    } finally {
      setLoading(false);
    }
  };

  const handleValidarCodigo = async () => {
    if (!inputCode || inputCode.length !== 6) {
      setError('Por favor ingresa un código de 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await AutorizacionesApi.procesarAutorizacion(inputCode, {
        autorizar: true,
        observaciones: `Autorización para: ${actionName}`,
      });

      if (response.Estado === 'AUTORIZADA') {
        setSuccess('¡Acción autorizada correctamente!');
        setActiveStep(2);
        setTimeout(() => {
          onAuthorized(inputCode);
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Código inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(authCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (countdown <= 300) return 'error'; // 5 minutos
    if (countdown <= 900) return 'warning'; // 15 minutos
    return 'success';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="authorization-modal-pro"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <LockIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Autorización Requerida
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actionName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Paso 0: Solicitar Código */}
        {activeStep === 0 && !authCode && (
          <Box textAlign="center" py={4}>
            <LockIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Esta acción requiere autorización
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Solicita un código de autorización para continuar
            </Typography>

            {Object.keys(actionData || {}).length > 0 && (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Detalles de la operación:
                </Typography>
                {Object.entries(actionData).map(([key, value]) => (
                  <Box
                    key={key}
                    display="flex"
                    justifyContent="space-between"
                    py={0.5}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {key}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleSolicitarCodigo}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
              >
                Solicitar Código
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setActiveStep(1)}
              >
                Tengo un Código
              </Button>
            </Box>
          </Box>
        )}

        {/* Paso 0: Mostrar Código Generado */}
        {activeStep === 0 && authCode && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" gutterBottom>
              Código de Autorización Generado
            </Typography>

            {waitingApproval && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Esperando aprobación del supervisor...</Typography>
                </Box>
              </Alert>
            )}
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 3,
                py: 3,
                px: 4,
                mb: 2,
                position: 'relative',
              }}
            >
              <Typography variant="h2" fontWeight={700} letterSpacing={4}>
                {authCode}
              </Typography>
              <IconButton
                onClick={handleCopyCode}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'white',
                }}
              >
                <ContentCopyIcon />
              </IconButton>
              {copied && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  ✓ Copiado
                </Typography>
              )}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
              mb={3}
            >
              <TimerIcon color={getTimeColor()} />
              <Typography variant="h6" color={`${getTimeColor()}.main`}>
                {formatTime(countdown)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Comparte este código con un supervisor autorizado
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(1)}
              size="large"
            >
              Ingresar Código de Autorización
            </Button>
          </Box>
        )}

        {/* Paso 1: Ingresar Código */}
        {activeStep === 1 && (
          <Box py={2}>
            <Typography variant="h6" textAlign="center" gutterBottom>
              Ingresar Código de Autorización
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 3 }}
            >
              Ingresa el código de 6 caracteres proporcionado por el supervisor
            </Typography>

            <TextField
              fullWidth
              label="Código de Autorización"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  fontSize: '2rem',
                  letterSpacing: '0.5rem',
                  fontWeight: 600,
                },
              }}
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Alert severity="info" icon={<PersonIcon />}>
              Un supervisor con permisos debe proporcionar este código
            </Alert>
          </Box>
        )}

        {/* Paso 2: Confirmación */}
        {activeStep === 2 && (
          <Box textAlign="center" py={4}>
            <LockOpenIcon
              sx={{ fontSize: 100, color: 'success.main', mb: 2 }}
            />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              ¡Autorización Confirmada!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              La acción ha sido autorizada correctamente
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {activeStep === 0 && !authCode && (
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
        )}

        {activeStep === 0 && authCode && (
          <Button onClick={onClose} disabled={loading}>
            Cerrar
          </Button>
        )}

        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={loading}>
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={handleValidarCodigo}
              disabled={loading || inputCode.length !== 6}
              startIcon={
                loading ? <CircularProgress size={20} /> : <LockOpenIcon />
              }
            >
              Autorizar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AuthorizationModal;